<template>
    <base-dialog :loading="loading" :value="dialog" :width="560" :title="dialogTitle" @input="onInput" @close="hide">
        <div v-if="personalRecord" class="pr-card">
            <div class="pr-icon">
                <v-icon size="20" color="#0B0E13">mdi-trophy-variant</v-icon>
            </div>
            <div class="pr-text">
                <span class="pr-label">{{ $t('common.personal_record') }}</span>
                <span class="pr-value">{{ personalRecord }}</span>
            </div>
        </div>

        <div v-if="history.length > 0" class="history-list">
            <div v-for="(item, i) in historyFiltered" :key="i" class="history-row">
                <div class="history-date">{{ item.date }}</div>
                <div class="history-data">
                    <div v-if="item.dataSet.repsType && item.dataSet.repsType.data" class="presc-line">
                        <v-icon x-small>{{ item.dataSet.repsType.icon }}</v-icon>
                        <span v-html="item.dataSet.repsType.data" />
                    </div>
                    <div v-if="item.dataSet.rpeType && item.dataSet.rpeType.data" class="presc-line">
                        <v-icon x-small>{{ item.dataSet.rpeType.icon }}</v-icon>
                        {{ item.dataSet.rpeType.data }}
                    </div>
                    <div v-if="item.dataSet.tempoType && item.dataSet.tempoType.data" class="presc-line">
                        <v-icon x-small>{{ item.dataSet.tempoType.icon }}</v-icon>
                        {{ item.dataSet.tempoType.data }}
                    </div>
                    <div v-if="item.dataSet.weight && item.dataSet.weight.data" class="presc-line">
                        <v-icon x-small>{{ item.dataSet.weight.icon }}</v-icon>
                        {{ item.dataSet.weight.data }}
                    </div>
                    <div v-if="item.dataSet.intensityType && item.dataSet.intensityType.data" class="presc-line">
                        <v-icon x-small>{{ item.dataSet.intensityType.icon }}</v-icon>
                        {{ item.dataSet.intensityType.data.replaceAll(':', '\'') }}
                    </div>
                    <div v-if="item.eachSide" class="presc-line">
                        {{ $t('exercises.each_side') }}
                    </div>
                    <div v-if="item.isCircuit" class="presc-line">
                        {{ exerciseSets(item) }} {{ item.eachSide ? `| ${$t('exercises.each_side')}` : '' }}
                    </div>
                </div>
            </div>
        </div>
        <skeleton-loader v-else-if="loading" type="exercise-history" />
        <div v-else class="history-empty">
            {{ $t('common.without_data') }}
        </div>
    </base-dialog>
</template>

<script>


import { db } from '@/utils/Firebase'
import { setTypeIcon, SET_NAMES, findSetData } from '@/utils/Exercises'
import SkeletonLoader from '../../common/SkeletonLoader.vue'
import BaseDialog from '../../common/BaseDialog.vue'

export default {
    components: { SkeletonLoader, BaseDialog },
    name: 'ExerciseHistoryDialog',
    props: {
        routine: {
            type: Object,
            default: null
        },
        collection: {
            type: String,
            default: 'assignedRoutines',
        },
    },
    data() {
        return {
            dialog: false,
            loading: false,
            exercise: null,
            routines: [],
            limit: 20,
        }
    },
    computed: {
        exerciseName(vm = this) {
            return vm.exercise?.name
        },
        dialogTitle(vm = this) {
            return vm.exerciseName ? `${vm.$t('common.history')} - ${vm.exerciseName}` : vm.$t('common.history')
        },
        history(vm = this) {
            if (!vm.exercise) return []
            let history = []
            vm.routines.forEach(routine => {
                routine?.blocks?.forEach(block => {
                    block?.exercises?.forEach(exercise => {
                        if (exercise.id === vm.exercise.id) {
                            const hasIntensity = !!exercise?.categories?.find(category => vm.categoryKey(category) === vm.CATEGORY.SPEED || vm.categoryKey(category) === vm.CATEGORY.RESISTANCE)
                            const isCircuit = vm.isCircuit(block, exercise)
                            const isTabata = block?.type === 'tabata'
                            let circuitSummary = ''
                            if (isCircuit) {
                                const cycles = block?.sets || 1
                                const value = exercise.config?.value
                                const type = exercise.config?.type ? vm.$t(`exercises.short_${exercise.config.type}`) : ''
                                const note = exercise.config?.note ? `(${exercise.config.note})` : ''
                                if (block?.type === 'amrap' || block?.type === 'emom') {
                                    circuitSummary = `${value}${note}${type}`
                                } else if (isTabata) {
                                    circuitSummary = `${cycles} ${vm.$tc('exercises.sets', { count: cycles })}`
                                } else {
                                    circuitSummary = `${cycles}x${value}${note}${type}`
                                }
                                circuitSummary += ` (${vm.$t('circuits.circuit')})`
                            }
                            history.push({
                                date: vm.$moment(routine.rawDate.full).format('DD MMMM YYYY'),
                                dateFormatted: vm.$moment(routine.rawDate.full).format('DD/MM/YY'),
                                eachSide: exercise.eachSide,
                                isCircuit,
                                isTabata,
                                circuitSummary,
                                dataSet: vm.getDataset(exercise.config?.sets || [], hasIntensity)
                            })
                        }
                    })
                })
            })
            return history
        },
        personalRecord(vm = this) {
            if (vm.history.length === 0) return null
            let maxWeight = 0
            let date = ''
            let maxWeightReps = 0
            vm.history.forEach(item => {
                if (item.dataSet.maxWeight) {
                    let weightValue = parseFloat(item.dataSet.maxWeight)
                    if (weightValue > maxWeight) {
                        maxWeight = weightValue
                        date = item.dateFormatted
                        maxWeightReps = item.dataSet?.maxWeightReps || 0
                    }
                }
            })
            return maxWeight > 0 ? `${maxWeightReps} X ${maxWeight}${vm.$t('exercises.short_weight')} - ${date}` : null
        },
        historyFiltered(vm = this) {
            return vm.history?.slice(0, 4) || []
        }
    },
    methods: {
        show(exercise) {
            const vm = this
            vm.exercise = exercise
            vm.dialog = true
            vm.loadExerciseHistory()
        },
        hide() {
            const vm = this
            if (!vm.dialog) return
            vm.dialog = false
            vm.exercise = null
        },
        onInput(val) {
            if (!val) this.hide()
        },
        async loadExerciseHistory() {
            const vm = this;
            if (!vm.routine) return;
            await vm.withLoading(async () => {
            let routinesRef = null

            if (vm.collection === 'assignedRoutines') {
                routinesRef = await db.collection(vm.collection)
                    .where('studentId', '==', vm.routine.studentId)
                    .where('routineNumber', '==', vm.routine.routineNumber)
                    .where('rawDate.full', '<', vm.routine.rawDate.full)
                    .orderBy('rawDate.full', 'desc')
                    .limit(vm.limit)
                    .get()
            } else {
                routinesRef = await db.collection(vm.collection)
                    .where('groupId', '==', vm.routine?.groupId || null)
                    .where('teamId', '==', vm.routine?.teamId || null)
                    .where('routineNumber', '==', vm.routine.routineNumber)
                    .where('rawDate.full', "<", vm.routine.rawDate.full)
                    .orderBy('rawDate.full', 'desc')
                    .limit(vm.limit)
                    .get()
            }

            let routines = []

            routinesRef.forEach(routine => {
                routines.push({ ...routine.data(), id: routine.id })
            })

            vm.routines = routines
            })
        },
        isCircuit(block, exercise) {
            return block?.isCircuit || exercise?.isCircuit || false
        },
        exerciseSets(item) {
            return item.circuitSummary || ''
        },
        getDataset(sets, hasIntensity) {
            const vm = this
            let data = {
                repsType: null,
                rpeType: null,
                tempoType: null,
                weight: null,
                maxWeight: null,
                maxWeightReps: null,
                intensityType: null,
                intensity: null,
                micropause: null,
            }
            sets.forEach((set, i) => {
                if (i === 0) {
                    let repsType = findSetData(set, hasIntensity ? SET_NAMES.REPS_INTENSITY : SET_NAMES.REPS)
                    if (repsType) data.repsType = { data: '', icon: setTypeIcon(repsType.short_name), short_name: repsType.short_name }
                    let rpeType = hasIntensity ? null : findSetData(set, SET_NAMES.RPE)
                    if (rpeType) data.rpeType = { data: '', icon: setTypeIcon(rpeType.short_name), short_name: rpeType.short_name }
                    let tempoType = findSetData(set, SET_NAMES.TEMPO)
                    if (tempoType) data.tempoType = { data: '', icon: setTypeIcon(tempoType.short_name), short_name: tempoType.short_name }
                    let weight = set.data && set.data.find(data => data.short_name === 'short_weight')
                    if (weight && !!weight.value) data.weight = {
                        data: '',
                        icon: setTypeIcon(weight.short_name),
                        short_name: weight.short_name
                    }
                    let intensityType = hasIntensity ? findSetData(set, SET_NAMES.INTENSITY) : null
                    if (intensityType) data.intensityType = {
                        data: '',
                        icon: setTypeIcon(intensityType.short_name),
                        short_name: intensityType.short_name
                    }
                    let micropause = set.data && set.data.find(data => data.short_name === 'short_micropause')
                    if (micropause) data.micropause = {
                        data: '',
                        icon: setTypeIcon(micropause.short_name),
                        short_name: micropause.short_name
                    }
                }

                let separation = sets.length > i + 1 ? '| ' : ''

                if (data.repsType) {
                    let valueReps = set.data && set.data.find(d => d.short_name === data.repsType.short_name)?.value
                    if (data.micropause) {
                        let valueMp = set.data && set.data.find(d => d.short_name === data.micropause.short_name)?.value + ""
                        let stringReps = `${valueReps}${vm.$t(`exercises.${data.repsType.short_name}`)}`
                        let stringMp = valueMp === "0" || valueMp === "" || valueMp === "00:00" ? '' : `${valueMp}${vm.$t(`exercises.${data.micropause.short_name}`)}`
                        data.repsType.data += `${stringReps} <sup>${stringMp}</sup> ${separation}`
                    } else {
                        data.repsType.data += `${valueReps}${vm.$t(`exercises.${data.repsType.short_name}`)} ${separation}`
                    }
                }

                if (data.rpeType) {
                    let valueRpe = set.data && set.data.find(d => d.short_name === data.rpeType.short_name)?.value + ""
                    if (valueRpe !== "0" && (data.rpeType.short_name === 'short_rpe' || data.rpeType.short_name === 'short_rir')) {
                        data.rpeType.data += `(${valueRpe}) ${separation}`
                    } else if (valueRpe !== "0") {
                        data.rpeType.data += `${valueRpe}${vm.$t(`exercises.${data.rpeType.short_name}`)} ${separation}`
                    }
                }

                if (data.tempoType) {
                    let valueTempo = set.data && set.data.find(d => d.short_name === data.tempoType.short_name)?.value + ""
                    if (valueTempo !== "0") data.tempoType.data += `${valueTempo}${vm.$t(`exercises.${data.tempoType.short_name}`)} ${separation}`
                }

                if (data.weight) {
                    let weight = set.data && set.data.find(d => d.short_name === data.weight.short_name)?.value + ""
                    if (weight !== "0") {
                        if (parseFloat(weight) > parseFloat(data.maxWeight || 0)) {
                            data.maxWeight = weight
                            let valueReps = set?.data?.find(d => d.short_name === data.repsType?.short_name)?.value || 0
                            data.maxWeightReps = valueReps
                        }
                        data.weight.data += `${weight}${vm.$t(`exercises.${data.weight.short_name}`)} ${separation}`
                    }
                }

                if (data.intensityType) {
                    let valueIntensity = set.data && set.data.find(d => d.short_name === data.intensityType.short_name)?.value + ""
                    if (valueIntensity !== "0") data.intensityType.data += `${valueIntensity}${vm.$t(`exercises.${data.intensityType.short_name}`)} ${separation}`
                }

                if (data.micropause && data.repsType?.short_name !== 'short_reps') {
                    let valueMicropause = set.data && set.data.find(d => d.short_name === data.micropause.short_name)?.value + ""
                    if (valueMicropause !== "0" && valueMicropause !== "" && valueMicropause !== "00:00") data.micropause.data += `${valueMicropause}${vm.$t(`exercises.${data.micropause.short_name}`)} ${separation}`
                }

            })

            return data
        }
    },
    watch: {

        routine() {
            if (this.dialog) this.loadExerciseHistory()
        }
    }
}

</script>

<style scoped>
.pr-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border-radius: 12px;
    background: var(--brand-gradient);
    margin-bottom: 16px;
}

.pr-icon {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.pr-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.pr-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: rgba(11, 14, 19, 0.65);
}

.pr-value {
    font-size: 15px;
    font-weight: 700;
    color: #0B0E13;
}

.history-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.history-row {
    display: flex;
    align-items: stretch;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 12px;
    overflow: hidden;
}

.history-date {
    flex: 0 0 92px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    text-transform: capitalize;
    line-height: 1.35;
    padding: 10px 8px;
    font-size: 12px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
    background: var(--v-field-base);
    border-right: 1px solid var(--v-outline-base);
}

.history-data {
    flex: 1 1 auto;
    min-width: 0;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 3px;
}

.presc-line {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    color: var(--v-txtPrimary-base);
}

.presc-line .v-icon {
    color: var(--v-accent-base) !important;
}

.presc-line >>> sup {
    color: var(--v-txtMuted-base);
}

.history-empty {
    text-align: center;
    padding: 28px 12px;
    font-size: 14px;
    color: var(--v-txtMuted-base);
}
</style>